import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useApi } from '@/hooks/useApi';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { withAuth } from '@/lib/apiInterceptor';
import { ParentDashboardFeatures } from '@/types/parentDashboardTypes';
import { InvoiceService, getInvoiceLineItems, createInvoicePaymentSession } from '@/services/invoiceService';
import { addDays } from 'date-fns';

// Type for parent portal settings
interface ParentPortalSettings {
  features?: {
    classEnrollments?: boolean;
    onlinePayments?: boolean;
    messaging?: boolean;
    documentAccess?: boolean;
    makeupClasses?: boolean;
    policyAgreements?: boolean;
  };
}

// Interface for upcoming classes
interface UpcomingClass {
  id: string;
  classId: string; // Add classId for view details functionality
  className: string;
  location: string;
  dayName: string;
  date: Date;
  startTime: string;
  endTime: string;
}

export const useParentDashboard = () => {
  const { user, isAdmin, userRole } = useAuth();
  const { withAuthGuard } = useApi();
  const { unreadCount } = useUnreadMessages();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([]);
  const [students, setStudents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [portalSettings, setPortalSettings] = useState<ParentPortalSettings | null>(null);
  const [familyName, setFamilyName] = useState('');
  const [features, setFeatures] = useState<ParentDashboardFeatures>({
    classEnrollments: true,
    onlinePayments: true,
    messaging: true,
    documentAccess: true,
    makeupClasses: true,
    policyAgreements: true
  });
  const [viewPreviewMode, setViewPreviewMode] = useState(false);

  // Check if user is a business admin viewing the parent portal
  const isBizAdmin = userRole === 'bizadmin' || userRole === 'owner';
  const isPreviewMode = isAdmin || isBizAdmin;

  const loadParentData = async () => {
      try {
        setLoading(true);

        // If admin or bizadmin is previewing, show empty state or use real data
        if (isPreviewMode) {
          // Set empty data for preview mode
          setUserData({
            id: 'preview-user',
            first_name: isBizAdmin ? 'Admin' : 'Preview',
            last_name: 'User',
            families: { name: 'Preview Family' }
          });
          setStudents([]);
          setUpcomingClasses([]);
          setInvoices([]);
          
          // Set default portal settings for preview mode
          setPortalSettings({
            features: {
              classEnrollments: true,
              onlinePayments: true,
              messaging: true,
              documentAccess: true,
              makeupClasses: true,
              policyAgreements: true
            }
          });
          
          setLoading(false);
          return;
        }

        // For actual parents, get family_id from user metadata
        const familyId = user?.user_metadata?.family_id;

        if (!familyId) {
          setError('No family_id found in user metadata - this user may not have family access');
          setLoading(false);
          return;
        }

        // Fetch parent data - using withAuthGuard to prevent 401 errors
        const { data: parentData, error: parentError } = await withAuthGuard(async () => {
          return supabase
            .from('parents')
            .select('*, families:family_id(*)')
            .eq('user_id', user.id);
        });

        if (parentError) {
          console.error('Error fetching parent data:', parentError);
          setError(parentError.message);
          setLoading(false);
          return;
        }

        // Handle array response to avoid .single() errors
        if (!parentData || parentData.length === 0) {
          console.error('No parent record found for user:', user.id);
          setError('Parent record not found. Please contact support.');
          setLoading(false);
          return;
        }

        // Use first parent record if multiple exist (shouldn't happen but handle gracefully)
        const parent = parentData[0];
        setUserData(parent);
        setFamilyName(parent.families?.name || 'Your Family');

        // Fetch organization settings to get parent portal configuration
        const { data: orgData, error: orgError } = await withAuthGuard(async () => {
          return supabase
            .from('organizations')
            .select('parent_portal_settings')
            .eq('id', parent.organization_id)
            .single();
        });
          
        if (!orgError && orgData) {
          // Fix: Ensure we're casting the portal settings to the correct type
          const portalData = orgData.parent_portal_settings as ParentPortalSettings | null;
          setPortalSettings(portalData || {
            features: {}
          });
        }

        // Fetch students first
        const { data: studentsData, error: studentsError } = await withAuthGuard(async () => {
          return supabase
            .from('students')
            .select('*')
            .eq('family_id', familyId);
        });

        if (studentsError) {
          console.error('Error fetching students:', studentsError);
          setStudents([]);
        } else {
          // Now fetch enrollments for each student separately to avoid complex join issues
          const studentsWithEnrollments = await Promise.all(
            (studentsData || []).map(async (student) => {
              try {
                const { data: enrollments, error: enrollError } = await supabase
                  .from('enrollments')
                  .select(`
                    id,
                    status,
                    enrolled_at,
                    class_id,
                    class:classes!enrollments_class_id_fkey(
                      id,
                      name,
                      description,
                      category,
                      level,
                      status,
                      price_cents,
                      trial_available,
                      dress_code,
                      is_event,
                      event_date,
                      event_start_time,
                      event_end_time
                    )
                  `)
                  .eq('student_id', student.id)
                  .neq('status', 'pending_payment'); // Exclude pending payment enrollments from parent portal

                if (enrollError) {
                  console.error('Error fetching enrollments for student:', student.id, enrollError);
                  return { ...student, enrollments: [] };
                }

                return { ...student, enrollments: enrollments || [] };
              } catch (err) {
                console.error('Error processing student enrollments:', err);
                return { ...student, enrollments: [] };
              }
            })
          );

          setStudents(studentsWithEnrollments);
        }

        // Fetch invoices for the family (excluding draft invoices)
        const { data: invoicesData, error: invoicesError } = await withAuthGuard(async () => {
          return supabase
            .from('invoices')
            .select('*')
            .eq('family_id', familyId)
            .neq('status', 'draft')
            .order('due_date', { ascending: false });
        });
        if (invoicesError) {
          console.error('Error fetching invoices:', invoicesError);
        } else {
          // Fetch payments for the family
          const paymentsData = await InvoiceService.getPaymentsByFamily(familyId);
          setPayments(paymentsData);
          // For each invoice, fetch line items with class/student names
          const invoicesWithLineItems = await Promise.all((invoicesData || []).map(async (invoice: any) => {
            const lineItems = await getInvoiceLineItems(invoice.id);
            // Find the earliest payment for this invoice with status 'completed' or 'paid'
            let paidOnDate = null;
            if (invoice.status === 'paid') {
              const payment = (paymentsData || []).find((p: any) =>
                p.invoice_id === invoice.id &&
                (p.status === 'completed' || p.status === 'paid') &&
                p.paid_at
              );
              if (payment) paidOnDate = payment.paid_at;
            }
            return { ...invoice, lineItems, paidOnDate };
          }));
          setInvoices(invoicesWithLineItems);
        }

        // Fetch upcoming classes for this week
        await fetchUpcomingClasses();

        // Get organization features
        try {
          const { data: orgFeaturesData } = await withAuthGuard(async () => {
            return supabase
              .from('organizations')
              .select('*')  // Select all columns instead of just 'settings'
              .eq('id', user.user_metadata.organization_id)
              .single();
          });

          // Cast parent_portal_settings to ParentPortalSettings type
          const portalSettings = orgFeaturesData?.parent_portal_settings as ParentPortalSettings;
          
          if (portalSettings?.features) {
            // Set features from organization parent_portal_settings
            const orgFeatures = portalSettings.features;
            setFeatures({
              classEnrollments: orgFeatures.classEnrollments !== false,
              onlinePayments: orgFeatures.onlinePayments !== false,
              messaging: orgFeatures.messaging !== false,
              documentAccess: orgFeatures.documentAccess !== false,
              makeupClasses: orgFeatures.makeupClasses !== false,
              policyAgreements: orgFeatures.policyAgreements !== false
            });
          }
        } catch (featureError) {
          console.error('Error fetching organization features:', featureError);
          // Continue with default features
        }

        // Check if we're in preview mode
        setViewPreviewMode(!!user.user_metadata.preview_mode);

      } catch (err: any) {
        console.error('Unexpected error in parent dashboard:', err);
        setError(`Unexpected error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadParentData();
  }, [user, isPreviewMode, isBizAdmin, withAuthGuard]);

  // Add a function to pay an invoice
  const payInvoice = async (invoice: any, customerEmail: string, successUrl: string, cancelUrl: string) => {
    return await createInvoicePaymentSession({
      invoiceId: invoice.id,
      familyId: invoice.family_id,
      organizationId: invoice.organization_id,
      amountCents: invoice.total_cents,
      customerEmail,
      successUrl,
      cancelUrl
    });
  };

  // Function to fetch upcoming classes for the current week
  const fetchUpcomingClasses = async () => {
    if (!user) return;

    try {
      // First get the parent record to find the family_id
      const { data: parentData, error: parentError } = await supabase
        .from('parents')
        .select('family_id')
        .eq('user_id', user.id);

      if (parentError) throw parentError;
      
      // Handle array response to avoid .single() errors
      if (!parentData || parentData.length === 0) {
        console.error('No parent record found for user in fetchUpcomingClasses:', user.id);
        return;
      }
      
      const parent = parentData[0];
      if (!parent?.family_id) return;

      // Find student IDs for this family
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('id')
        .eq('family_id', parent.family_id);

      if (studentsError) throw studentsError;
      if (!studentsData || studentsData.length === 0) {
        setUpcomingClasses([]);
        return;
      }

      const studentIds = studentsData.map(student => student.id);

      // Get enrollments for these students
      const { data: enrollmentsData, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select('class_id')
        .in('student_id', studentIds)
        .eq('status', 'active');

      if (enrollmentsError) throw enrollmentsError;
      if (!enrollmentsData || enrollmentsData.length === 0) {
        setUpcomingClasses([]);
        return;
      }

      const classIds = enrollmentsData.map(enrollment => enrollment.class_id);

      // Get class information first
      const { data: classesData, error: classesError } = await supabase
        .from('classes')
        .select('id, name, location')
        .in('id', classIds);
        
      if (classesError) throw classesError;
      
      // Create a map of class information for easy lookup
      const classInfoMap = classesData.reduce((map, cls) => {
        map[cls.id] = { name: cls.name, location: cls.location || 'No location specified' };
        return map;
      }, {} as Record<string, { name: string, location: string }>);

      // Get schedules for these classes
      const { data: schedulesData, error: schedulesError } = await supabase
        .from('class_schedules')
        .select('id, name, weekday, start_time, end_time, class_id')
        .in('class_id', classIds);

      if (schedulesError) throw schedulesError;
      
      // Process schedules into upcoming classes for this week
      const today = new Date();
      const todayDayOfWeek = today.getDay(); // 0 for Sunday, 6 for Saturday
      const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      const upcoming: UpcomingClass[] = [];
      
      schedulesData.forEach((schedule) => {
        const classInfo = classInfoMap[schedule.class_id];
        if (!classInfo) return; // Skip if class info not found
        
        let daysToAdd = schedule.weekday - todayDayOfWeek;
        if (daysToAdd < 0) daysToAdd += 7; // Add a week if the day has passed
        
        // Only include classes happening within the next 7 days (this week)
        if (daysToAdd <= 7) {
          const nextDate = addDays(today, daysToAdd);
          
          upcoming.push({
            id: schedule.id,
            classId: schedule.class_id, // Add the actual class ID
            className: classInfo.name,
            location: classInfo.location,
            dayName: DAYS[schedule.weekday],
            date: nextDate,
            startTime: schedule.start_time,
            endTime: schedule.end_time
          });
        }
      });
      
      // Sort by upcoming date and time
      upcoming.sort((a, b) => {
        if (a.date.getTime() !== b.date.getTime()) {
          return a.date.getTime() - b.date.getTime();
        }
        return a.startTime.localeCompare(b.startTime);
      });
      
      setUpcomingClasses(upcoming);
    } catch (error) {
      console.error('Error fetching upcoming classes:', error);
      setUpcomingClasses([]);
    }
  };

  // Add a function to refresh invoices and payments
  const refreshDashboard = async () => {
    if (!user) return;
    const familyId = user?.user_metadata?.family_id;
    if (!familyId) return;
    // Fetch invoices
    const { data: invoicesData, error: invoicesError } = await supabase
      .from('invoices')
      .select('*')
      .eq('family_id', familyId)
      .order('due_date', { ascending: false });
    // Fetch payments
    const paymentsData = await InvoiceService.getPaymentsByFamily(familyId);
    setPayments(paymentsData);
    if (!invoicesError) {
      const invoicesWithLineItems = await Promise.all((invoicesData || []).map(async (invoice: any) => {
        const lineItems = await getInvoiceLineItems(invoice.id);
        let paidOnDate = null;
        if (invoice.status === 'paid') {
          const payment = (paymentsData || []).find((p: any) =>
            p.invoice_id === invoice.id &&
            (p.status === 'completed' || p.status === 'paid') &&
            p.paid_at
          );
          if (payment) paidOnDate = payment.paid_at;
        }
        return { ...invoice, lineItems, paidOnDate };
      }));
      setInvoices(invoicesWithLineItems);
    }
  };

  // Add a function to refresh student enrollments only
  const refreshStudentEnrollments = async () => {
    if (!user || isPreviewMode) return;

    const familyId = user?.user_metadata?.family_id;
    if (!familyId) return;

    try {
      // Fetch students first
      const { data: studentsData, error: studentsError } = await withAuthGuard(async () => {
        return supabase
          .from('students')
          .select('*')
          .eq('family_id', familyId);
      });

      if (studentsError) {
        console.error('Error fetching students for enrollment refresh:', studentsError);
        return;
      }

      // Now fetch fresh enrollments for each student
      const studentsWithEnrollments = await Promise.all(
        (studentsData || []).map(async (student) => {
          try {
            const { data: enrollments, error: enrollError } = await supabase
              .from('enrollments')
              .select(`
                id,
                status,
                enrolled_at,
                class_id,
                class:classes!enrollments_class_id_fkey(
                  id,
                  name,
                  description,
                  category,
                  level,
                  status,
                  price_cents,
                  trial_available,
                  dress_code,
                  is_event,
                  event_date,
                  event_start_time,
                  event_end_time
                )
              `)
              .eq('student_id', student.id)
              .neq('status', 'pending_payment'); // Exclude pending payment enrollments from parent portal

            if (enrollError) {
              console.error('Error fetching enrollments for student:', student.id, enrollError);
              return { ...student, enrollments: [] };
            }

            return { ...student, enrollments: enrollments || [] };
          } catch (err) {
            console.error('Error processing student enrollments:', err);
            return { ...student, enrollments: [] };
          }
        })
      );

      setStudents(studentsWithEnrollments);
    } catch (err) {
      console.error('Error refreshing student enrollments:', err);
    }
  };

  // Add a function to refresh all data
  const refreshData = async () => {
    await loadParentData();
  };

  return {
    userData,
    loading,
    error: isPreviewMode ? null : error, // Don't show errors in preview mode
    unreadMessages: unreadCount,
    upcomingClasses,
    students,
    invoices,
    payments,
    payInvoice,
    portalSettings,
    familyName,
    features,
    viewPreviewMode,
    refreshDashboard,
    refreshData,
    refreshStudentEnrollments
  };
};
